import React from 'react';
import './DataTable.css';

const DataTable = ({ 
  columns = [], 
  data = [], 
  emptyMessage = "No data available", 
  maxHeight = "600px" 
}) => {
  const safeToString = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    if (value instanceof Date) return value.toLocaleString();
    
    // Handle Firestore timestamp objects
    if (typeof value === 'object' && 'toDate' in value) {
      try {
        return value.toDate().toLocaleString();
      } catch (error) {
        console.error('Error converting timestamp:', error);
        return 'Invalid Date';
      }
    }
    
    // Handle objects with seconds/nanoseconds (Firestore timestamp format)
    if (typeof value === 'object' && 'seconds' in value) {
      try {
        return new Date(value.seconds * 1000).toLocaleString();
      } catch (error) {
        console.error('Error converting timestamp:', error);
        return 'Invalid Date';
      }
    }
    
    // For other objects, try to stringify safely
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return '[Object]';
      }
    }
    
    return String(value);
  };

  const renderCellContent = (row, column, index) => { // Added index parameter
    try {
      if (column.render) {
        return column.render(row, index); // Pass index to render function
      }
      
      const value = row[column.accessor];
      
      if (value === undefined || value === null) {
        return <span className="text-muted">N/A</span>;
      }
      
      // Handle Date objects
      if (value instanceof Date) {
        return value.toLocaleString();
      }
      
      // Handle Firestore timestamps (if they somehow weren't converted)
      if (typeof value === 'object' && 'toDate' in value) {
        try {
          return value.toDate().toLocaleString();
        } catch (error) {
          console.error('Error converting timestamp in render:', error);
          return <span className="text-danger">Invalid Date</span>;
        }
      }
      
      // Handle objects with seconds/nanoseconds (Firestore timestamp format)
      if (typeof value === 'object' && 'seconds' in value) {
        try {
          return new Date(value.seconds * 1000).toLocaleString();
        } catch (error) {
          console.error('Error converting timestamp in render:', error);
          return <span className="text-danger">Invalid Date</span>;
        }
      }
      
      // For any other object, safely convert to string
      if (typeof value === 'object') {
        return safeToString(value);
      }
      
      return safeToString(value);
    } catch (error) {
      console.error('Error rendering table cell:', error);
      return <span className="text-danger">Error</span>;
    }
  };

  return (
    <div className="table-container">
      <div className="table-scroll" style={{ maxHeight }}>
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={`header-${index}`}>{column.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {columns.map((column, colIndex) => (
                    <td key={`cell-${rowIndex}-${colIndex}`}>
                      {renderCellContent(row, column, rowIndex)} {/* Pass rowIndex here */}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-5">
                  <div className="empty-message">
                    <i className="bi bi-database-x"></i>
                    <h4>{emptyMessage || "No data available"}</h4>
                    <p className="text-muted">Try adjusting your search or creating new records</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable;
